/**
 * Seitenkopf des Kapitels — Persona-Umbau B1 (28.10.2026, Plan 2026-10-28-persona-umbau-paket-1-2), drei Kacheln, der Wert
 * groß, die Menge klein (Leitregel 1), jede mit ihrem Fenster im Etikett (Regel 2):
 *  1. Letzter Termin: Erwarteter Wert, ▲/▼ gegen den Typischen Termin der Webinar-Tabelle (dieselbe Antwort), Titel im Etikett.
 *  2. Empfehlung für den nächsten freien Sonntag: Platz 1 mit Prognose, „gleichauf" aus derselben Rechnung wie die Karte.
 *  3. Anteil Kanal Webinar am Erwarteten Wert der 12 Monate; Summe und Termine in der Unterzeile.
 * Die frühere Kachel „Die letzten 8 fertigen Termine" entfällt (Plan B1). Liest dieselben Antworten wie die Karten
 * (Ranking geteilt, Produkte, Teilnahme); fehlt eine, bleibt ihre Kachel leer statt falsch.
 */
import { FC } from 'react';
import { Box } from '@mui/material';
import { Kachel, KACHEL_RASTER, spanne, tEuro } from '../../../components/auswertungen/kennzahlTeile';
import { formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { fetchProdukte, fetchTeilnahme, fetchWebinarRankingGeteilt } from '../../../helpers/auswertungenData';
import { abstandTypisch, produktLabel } from './befunde';
import { gleichaufBis, sonntagKurz } from './empfehlungText';
import { GegenTypisch, int, offenBeiTermin, tagMitWochentag, useLaden, ZahlenOffen, ZuordnungLaeuft } from './shared';

/** „Gut die Hälfte" usw. — nur eine Umschreibung der live gerechneten Quote, die Zahl steht daneben. */
export function anteilWorte(q: number): string {
  if (q >= 0.7) return 'Gut zwei Drittel';
  if (q >= 0.6) return 'Knapp zwei Drittel';
  if (q >= 0.5) return 'Gut die Hälfte';
  if (q >= 0.45) return 'Knapp die Hälfte';
  if (q >= 0.3) return 'Gut ein Drittel';
  return 'Ein kleiner Teil';
}

const SeitenKopf: FC = () => {
  const ranking = useLaden(fetchWebinarRankingGeteilt);
  const produkte = useLaden(fetchProdukte);
  const teilnahme = useLaden(fetchTeilnahme);
  const k = ranking.data?.seitenkopf;
  if (!k || !ranking.data) return null;
  const d = ranking.data;
  const kanal = produkte.data?.kanal_webinar;
  const anteil = kanal && kanal.gesamt > 0 ? kanal.wert / kanal.gesamt : null;
  const ohneTermin = produkte.data ? Math.max(0, produkte.data.summary.webinar_anfos - k.anfos) : null;
  const letzter = d.rows[0] ?? null;
  const su = letzter ? teilnahme.data?.rows.find((r) => r.webinar_id === letzter.webinar_id) : undefined;
  const offen = letzter ? offenBeiTermin(d.zuordnung, letzter.webinar_id) : 0;
  const typisch = d.typisch ?? null;
  // Empfehlung: die Tabelle der Karte gilt dem nächsten FREIEN Sonntag (`prognose_frei`, sonst `prognose`).
  const tabelle = d.prognose_frei ?? d.prognose;
  const erster = tabelle?.kandidaten.find((x) => x.rang === 1) ?? null;
  const gleichauf = tabelle ? gleichaufBis(tabelle.kandidaten) : 1;
  const angelegt = d.prognose_frei && d.prognose ? d.prognose.geplant : [];
  const name = (code: string) => produktLabel(code, d.rows.find((r) => r.plx_product === code)?.product_name);
  // B58: Singular/Plural, und die Abweichung als das benennen, was sie ist — zwei verschieden geschnittene Mengen
  // (Kanal nach Bestelldatum, Termine nach Termindatum), keine Zählung „ohne Termin" (B52).
  const anteilTip =
    kanal &&
    `${TERMS.anteilWertUeberWebinare.tip} Kanal Webinar: ${tEuro(kanal.wert)} von ${tEuro(kanal.gesamt)}. Die Summe darunter zählt die Anfos der Termine dieser 12 Monate (nach Termindatum), der Kanal die Webinar-Anfos nach Bestelldatum${
      ohneTermin ? ` — ${int(ohneTermin)} ${ohneTermin === 1 ? 'Anfo' : 'Anfos'} Unterschied` : ''
    }.`;
  const fenster = produkte.data ? spanne(produkte.data.window.von, produkte.data.vergleich?.jetzt.letzter_tag ?? produkte.data.window.asOf) : null;
  const ohneZahl = !!letzter && offen > 0 && letzter.anfos === 0;
  return (
    <Box sx={{ ...KACHEL_RASTER, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, mb: 3 }}>
      <Kachel
        wert={!letzter ? '—' : ohneZahl ? '—' : tEuro(letzter.wert_summe_fest ?? null)}
        label={letzter ? `Letzter Termin, ${tagMitWochentag(letzter.start_date)}: ${letzter.public_title}` : 'Letzter Termin'}
        tip={`${TERMS.wertSumme.tip} ${TERMS.gegenTypisch.tip}`}
        sub={
          letzter ? (
            <>
              {ohneZahl ? (
                // B14: eine 0 vor dem nächsten Zuordnungslauf ist kein Ergebnis — statt „0,0 T€ · 0 Anfos" der Hinweis.
                <Box component="span" sx={{ display: 'block', color: 'text.secondary' }}>
                  Anfos folgen nach dem nächsten Zuordnungslauf
                </Box>
              ) : (
                typisch && (
                  <GegenTypisch
                    abstand={abstandTypisch(letzter.wert_summe_fest ?? null, typisch.wert_summe_fest)}
                    typischText={tEuro(typisch.wert_summe_fest)}
                    offen={letzter.is_young}
                  />
                )
              )}
              <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                {int(letzter.anfos)} Anfos
                {letzter.provisional > 0 && ` (davon ${int(letzter.provisional)} vorläufig, ohne Wert)`}
                {su && su.show_up !== null && ` · ${TERMS.showUpTermin.label} ${formatPct(su.show_up, 0)}`}
                {su && su.show_up !== null && teilnahme.data?.typisch_show_up != null && ` (typisch ${formatPct(teilnahme.data.typisch_show_up, 0)})`}
                <ZahlenOffen bis={letzter.zahlen_offen_bis} />
              </Box>
              <ZuordnungLaeuft n={offen} lauf={d.zuordnung?.lauf} />
            </>
          ) : undefined
        }
      />
      <Kachel
        wert={erster && erster.median !== null ? `≈ ${tEuro(erster.median)}` : '—'}
        label={tabelle ? `${TERMS.empfehlungKachel.label} für ${sonntagKurz(tabelle.zieltermin)}` : TERMS.empfehlungKachel.label}
        tip={`${TERMS.empfehlungKachel.tip} ${TERMS.prognoseTermin.tip}`}
        sub={
          tabelle ? (
            <>
              {erster ? (
                <Box component="span" sx={{ display: 'block', color: 'text.primary' }}>
                  {erster.title}
                  {erster.plx_product ? ` (${name(erster.plx_product)})` : ''}
                </Box>
              ) : (
                <Box component="span" sx={{ display: 'block' }}>Kein Webinar frei — alle Kandidaten sind gesperrt.</Box>
              )}
              {gleichauf > 1 && (
                <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                  Plätze 1–{gleichauf} gleichauf
                </Box>
              )}
              {angelegt.length > 0 && d.prognose && (
                <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                  {sonntagKurz(d.prognose.zieltermin)} schon angelegt: {angelegt.map((g) => `${g.title}${g.plx_product ? ` (${g.plx_product})` : ''}`).join(', ')}
                </Box>
              )}
            </>
          ) : undefined
        }
      />
      <Kachel
        wert={anteil !== null ? formatPct(anteil * 100, 0) : '—'}
        label={`${TERMS.anteilWertUeberWebinare.label}, letzte 12 Monate${fenster ? ` (Bestellungen ${fenster})` : ''}`}
        tip={anteilTip || TERMS.anteilWertUeberWebinare.tip}
        sub={
          <>
            {anteil !== null && <Box component="span" sx={{ display: 'block' }}>{anteilWorte(anteil)} des Erwarteten Werts aller Anfos.</Box>}
            <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
              Termine der 12 Monate: {tEuro(k.wert_summe_fest)} aus {int(k.termine)} Terminen · {int(k.anfos)} Anfos
            </Box>
          </>
        }
      />
    </Box>
  );
};

export default SeitenKopf;
